import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { initiateRegistrationApi, verifyRegistrationOtpApi } from '../services/api';
import { fetchLookupDataApi } from '../services/lookupApi';
import Select from 'react-select';
// import '../components/css/RegisterPage.css';
import TermsCom from "../components/TermsCom";

const ROLES = {
    TALENT: { name: 'Talent', desc: '', code: '300' },
    PRODUCER: { name: 'Director/Producer', desc: '', code: '200' },
    CASTING: { name: 'Casting Director', desc: '', code: '400' },
};

const RegisterPage = () => {
    // Form States
    const [step, setStep] = useState(1);
    const [fullName, setFullName] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState(''); 
    const [tnc, setTnc] = useState(false);
    const [selectedRoleCode, setSelectedRoleCode] = useState(ROLES.TALENT.code);
    const [otp, setOtp] = useState('');
    
    const [selectedTalent, setSelectedTalent] = useState(null);
    
    // UI & API States
    const [userId, setUserId] = useState(null); 
    const [showTalentList, setShowTalentList] = useState(true); 
    const [lookupOptions, setLookupOptions] = useState({ talent: [] });
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Frontend Validation State
    const [errors, setErrors] = useState({});
    
    // Terms & Conditions Modal State
    const [showTncModal, setShowTncModal] = useState(false);
    const [isTncRead, setIsTncRead] = useState(false); // ✅ Added state to track scrolling
    
    const navigate = useNavigate();

    useEffect(() => {
        const loadTalent = async () => {
            try {
                const res = await fetchLookupDataApi('jobTalent');
                const options = res.data.data.map(item => ({
                    value: item.lookupValue,
                    label: item.lookupLabel
                }));
                setLookupOptions({ talent: options });
            } catch (err) {
                console.error("API failed, using static");
                setLookupOptions({
                    talent: [
                        { value: 'actor', label: 'Actor' },
                        { value: 'comedian', label: 'Comedian' },
                        { value: 'castingDirector', label: 'Casting Director' },
                        { value: 'model', label: 'Model' },
                        { value: 'scriptWriter', label: 'Script Writer' },
                        { value: 'filmAndStageCrew', label: 'Film And Stage Crew' },
                        { value: 'dancer', label: 'Dancer' },
                        { value: 'musician', label: 'Musician' },
                        { value: 'singer', label: 'Singer' },
                        { value: 'anchorEmcee', label: 'Anchor / Emcee' },
                        { value: 'sportsCommentator', label: 'Sports Commentator' },
                        { value: 'assistantDirector', label: 'Assistant Director' },
                        { value: 'director', label: 'Director' },
                        { value: 'producer', label: 'Producer' },
                        { value: 'choreographer', label: 'Choreographer' },
                        { value: 'cinematographer', label: 'Cinematographer' },
                        { value: 'editor', label: 'Editor' },
                        { value: 'soundEngineer', label: 'Sound Engineer' },
                        { value: 'lightingTechnician', label: 'Lighting Technician' },
                        { value: 'costumeDesigner', label: 'Costume Designer' }
                    ]
                });
            }
        };

        loadTalent();
    }, []);

    // ----------------------------------------
    // FRONTEND VALIDATION LOGIC
    // ----------------------------------------
    const validateForm = () => {
        const newErrors = {};

        if (!fullName.trim()) newErrors.fullName = "Full name is required.";
        
        const mobileRegex = /^[0-9]{10,15}$/;
        if (!mobileRegex.test(mobile)) newErrors.mobile = "Enter a valid mobile number (10-15 digits).";
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) newErrors.email = "Enter a valid email address.";
        
        if (password.length < 6) newErrors.password = "Password must be at least 6 characters.";
        
        if (selectedRoleCode === ROLES.TALENT.code && !selectedTalent) {
            newErrors.talent = "Please select your primary talent.";
        }

        if (!tnc) newErrors.tnc = "You must accept the terms and conditions.";

        setErrors(newErrors);
        
        return Object.keys(newErrors).length === 0;
    };

    // ----------------------------------------
    // API CALLS
    // ----------------------------------------
    const handleInitiateRegistration = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        const job_talent = selectedTalent ? [selectedTalent.value] : [];
        
        try {
            const { data } = await initiateRegistrationApi({ 
                fullName, email, mobileNo: mobile, roleCode: selectedRoleCode, password, talent: job_talent 
            });

            if (data.userId) {
                setUserId(data.userId); 
                setMessage(data.message || 'OTP sent to your email.');
                setIsError(false);
                setStep(2);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || 'Registration failed.');
            setIsError(true);
        }
        setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!userId) { setMessage('Invalid session. Please try again.'); setIsError(true); return; }
        if (otp.length < 6) { setMessage('Enter a valid 6-digit OTP.'); setIsError(true); return; }

        setLoading(true);
        try {
            await verifyRegistrationOtpApi({ userId: userId, otp });
            setMessage('Account verified! Redirecting to login...');
            setIsError(false);
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setMessage(err.response?.data?.message || 'OTP verification failed.');
            setIsError(true);
        }
        setLoading(false);
    };

    // ----------------------------------------
    // TnC HANDLERS
    // ----------------------------------------
    const openTncModal = (e) => {
        e.preventDefault();
        setIsTncRead(false); // Reset read status when opened
        setShowTncModal(true);
    };

    const handleAcceptTerms = () => {
        setTnc(true);
        setShowTncModal(false);
        setErrors(prev => ({ ...prev, tnc: null })); 
    };

    // ✅ Triggered when user scrolls inside the Terms modal
    const handleTncScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Adding a 5px buffer to account for minor rounding/zoom issues in some browsers
        if (scrollHeight - scrollTop <= clientHeight + 5) {
            setIsTncRead(true);
        }
    };

    const renderStep1 = () => (
        <Form onSubmit={handleInitiateRegistration}>
            <label className="form-label-custom">I am a</label>
           
            <div className="role-selection-wrapper mb-3 regis">
                {Object.values(ROLES).map((role) => (
                    <div 
                        key={role.code}
                        className={`role-card-item ${selectedRoleCode === role.code ? 'active' : ''}`}
                        onClick={() => {
                            setSelectedRoleCode(role.code);
                            if (role.code === ROLES.TALENT.code) {
                                setShowTalentList(true);
                            } else {
                                setShowTalentList(false);
                                setSelectedTalent(null);
                                setErrors(prev => ({...prev, talent: null})); 
                            }
                        }}
                    >
                        <h6>{role.name}</h6>
                        <span>{role.desc}</span>
                    </div>
                ))}
            </div>

            {showTalentList && (
                <Form.Group className="mb-2 text-start" >
                    <Form.Label className="form-label-custom">Select Talent <span className="text-danger">*</span></Form.Label>
                    <div className={errors.talent ? 'select-error-wrapper' : ''}>
                        <Select
                            options={lookupOptions.talent}
                            value={selectedTalent}
                            onChange={(selected) => {
                                setSelectedTalent(selected);
                                if(selected) setErrors(prev => ({ ...prev, talent: null }));
                            }}
                            placeholder="Select primary talent..."
                            className="custom-select"
                            classNamePrefix="react-select"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: '#2a2d30 !important', 
                                    borderColor: '#444',
                                    color: 'white'
                                }),
                                menu: (base) => ({ ...base, backgroundColor: '#2a2d30' }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused ? '#3e4247' : '#2a2d30',
                                    color: 'white'
                                }),
                                singleValue: (base) => ({ ...base, color: 'white' }),
                            }}
                        />
                    </div>
                    {errors.talent && <span className="error-text-custom">{errors.talent}</span>}
                </Form.Group>
            )}

            <Form.Group className="mb-2 text-start">
                <Form.Label className="form-label-custom">Full Name <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                    type="text" 
                    placeholder="John Doe" 
                    value={fullName} 
                    onChange={(e) => {
                        setFullName(e.target.value);
                        if(errors.fullName) setErrors(prev => ({...prev, fullName: null}));
                    }} 
                    className={`form-ctrl-login-custom ${errors.fullName ? 'error-input-custom' : ''}`} 
                />
                {errors.fullName && <span className="error-text-custom">{errors.fullName}</span>}
            </Form.Group>

            <Form.Group className="mb-2 text-start">
                <Form.Label className="form-label-custom">Mobile Number <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                    type="tel" 
                    placeholder="+91..." 
                    value={mobile} 
                    onChange={(e) => {
                        setMobile(e.target.value.replace(/\D/g, '')); 
                        if(errors.mobile) setErrors(prev => ({...prev, mobile: null}));
                    }} 
                    className={`form-ctrl-login-custom ${errors.mobile ? 'error-input-custom' : ''}`} 
                />
                {errors.mobile && <span className="error-text-custom">{errors.mobile}</span>}
            </Form.Group>

            <Form.Group className="mb-2 text-start">
                <Form.Label className="form-label-custom">Email Address <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                    type="email" 
                    placeholder="you@example.com" 
                    value={email} 
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if(errors.email) setErrors(prev => ({...prev, email: null}));
                    }} 
                    className={`form-ctrl-login-custom ${errors.email ? 'error-input-custom' : ''}`} 
                />
                {errors.email && <span className="error-text-custom">{errors.email}</span>}
            </Form.Group>

            <Form.Group className="mb-2 text-start">
                <Form.Label className="form-label-custom">Password <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                    type="password" 
                    placeholder="Min 6 characters" 
                    value={password} 
                    onChange={(e) => {
                        const newPassword = e.target.value;
                        setPassword(newPassword);
                        setConfirmPassword(newPassword); 
                        if(errors.password) setErrors(prev => ({...prev, password: null}));
                    }} 
                    className={`form-ctrl-login-custom ${errors.password ? 'error-input-custom' : ''}`} 
                />
                {errors.password && <span className="error-text-custom">{errors.password}</span>}
            </Form.Group>
            
            <Form.Group className="mb-2 text-start">
                <Form.Check 
                    type="checkbox" 
                    id="tnc" 
                    checked={tnc} 
                    // Prevent users from directly checking the box without opening the modal
                    onChange={(e) => {
                        if (e.target.checked) {
                            openTncModal(e);
                        } else {
                            setTnc(false);
                        }
                    }} 
                    label={
                        <span className="small text-white ms-1">
                            I agree to the <span className="text-primary text-decoration-underline" style={{cursor: 'pointer'}} onClick={openTncModal}>Terms and Conditions</span>
                        </span>
                    } 
                />
                {errors.tnc && <span className="error-text-custom">{errors.tnc}</span>}
            </Form.Group>

            <Button type="submit" className="btn-auth-primary w-100 mt-2" disabled={loading}>
                {loading ? <Spinner className="text-white" size="sm" animation="border" /> : 'Register & Get OTP'}
            </Button>
            <p className="auth-footer-link text-white text-center mt-4">Already have an account? <Link to="/login">Sign in</Link></p>
        </Form>
    );
    
    return (
         <AuthLayout 
            pageTitle="Register" sidebarTitle="Create Your Account"
        >
            {message && <Alert variant={isError ? "danger" : "success"} className="py-2 small mb-3">{message}</Alert>}
            <div className='registerPage'>
                {step === 1 ? renderStep1() : (
                    <Form onSubmit={handleVerifyOtp}>
                        <p className="text-white small mb-3 text-center">Enter the 6-digit code sent to <b>{email}</b>.</p>
                        <Form.Control 
                            type="text" 
                            placeholder="------" 
                            value={otp} 
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0,6))} 
                            maxLength="6"
                            className="form-ctrl-login-custom text-center mb-4" 
                            style={{ letterSpacing: '0.5em', fontSize: '1.5rem' }}
                        />
                        <Button type="submit" className="btn-auth-primary w-100" disabled={loading}>
                            {loading ? <Spinner className="text-white" size="sm" animation="border" /> : 'Verify & Complete'}
                        </Button>
                        <div className="text-center mt-3">
                            <Button variant="link" className="text-white small p-0 text-decoration-none" onClick={() => setStep(1)}>
                                &larr; Edit details / Back
                            </Button>
                        </div>
                    </Form>
                )}
            </div>

            <Modal 
                show={showTncModal} 
                onHide={() => setShowTncModal(false)} 
                size="lg" 
                centered 
                backdrop="static"
                data-bs-theme="dark"
            >
                <Modal.Header closeButton className="bg-dark text-white border-secondary">
                    <Modal.Title>Terms and Conditions</Modal.Title>
                </Modal.Header>

                {/* ✅ ADDED onScroll to track when user reaches the bottom */}
                <Modal.Body 
                    className="bg-dark p-3 text-white" 
                    style={{ height: '60vh', overflowY: 'auto' }}
                    onScroll={handleTncScroll}
                >
                    <TermsCom />
                </Modal.Body>

                <Modal.Footer className="bg-dark border-secondary">
                    {/* ✅ Display a helpful message if they haven't scrolled to the bottom */}
                    {!isTncRead && <span className="text-warning small me-auto">Please scroll to the bottom to accept.</span>}
                    
                    <Button variant="outline-light" onClick={() => setShowTncModal(false)}>
                        Close
                    </Button>

                    {/* ✅ Disable Accept button until isTncRead is true */}
                    {Boolean(isTncRead) && (
                        <Button variant="primary" onClick={handleAcceptTerms} disabled={!isTncRead}>
                            I Accept
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </AuthLayout>
    );
};

export default RegisterPage;